import React from 'react';
import { Button } from '@/components/ui/button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Label included in the logged error, to identify which area failed. */
  name?: string;
  /** Optional custom fallback UI; defaults to a friendly recover screen. */
  fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Catches render/lifecycle exceptions in its subtree so one failure can't
 * unmount the whole React tree and blank the screen. The error is recorded
 * (console) and a recoverable fallback is shown instead.
 *
 * Note: React error boundaries only catch errors thrown during rendering,
 * lifecycle methods, and constructors of the components below them. They do NOT
 * catch errors inside event handlers or async callbacks (fetch handlers, etc.) —
 * but those don't crash the tree anyway; they're handled with try/catch where
 * they occur. This is the safety net for the render-time crashes (like passing a
 * non-string into JSX) that DO blank the screen.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Record without crashing. Shows up in the browser console / dev tools; if
    // you later add a backend error-logging endpoint, POST it here too.
    const where = this.props.name ? `: ${this.props.name}` : '';
    console.error(`[ErrorBoundary${where}]`, error, info.componentStack);
  }

  handleReset = () => this.setState({ hasError: false });

  render() {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return <>{this.props.fallback}</>;

    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-background px-6 text-center">
        <div className="w-full max-w-md">
          <h1 className="text-xl font-semibold tracking-tight">Something went wrong</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            This part of the page hit an unexpected error. Your data is safe — try again, or reload the page.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button onClick={this.handleReset}>Try again</Button>
            <Button variant="outline" onClick={() => window.location.reload()}>
              Reload page
            </Button>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
